import React, { useEffect, useState } from "react";
import { fetchTestimonials, addTestimonial } from "../api";
import SEO from './SEO';
import "bootstrap/dist/css/bootstrap.min.css";
import "../style/Testimonial.css";

const Testimonials = ({ currentLanguage }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [filteredTestimonials, setFilteredTestimonials] = useState([]);
  const [newTestimonial, setNewTestimonial] = useState({
    name: "",
    content: "",
    language: currentLanguage,
  });
  const [modalContent, setModalContent] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch testimonials and filter only active ones
  useEffect(() => {
    fetchTestimonials()
      .then((response) => {
        const activeTestimonials = response.data.filter(
          (testimonial) => testimonial.is_active === true
        );
        setTestimonials(activeTestimonials);
        filterTestimonials(activeTestimonials, currentLanguage);
      })
      .catch((error) => console.error("Error fetching testimonials:", error));
  }, [currentLanguage]);

  // Filter testimonials based on the current language
  const filterTestimonials = (testimonials, language) => {
    const filtered = testimonials.filter(
      (testimonial) => testimonial.language === language
    );
    setFilteredTestimonials(filtered);
  };

  // Handle form inputs
  const handleInputChange = (e) => {
    setNewTestimonial({ ...newTestimonial, [e.target.name]: e.target.value });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await addTestimonial(newTestimonial);
      setNewTestimonial({ name: "", content: "", language: currentLanguage });
      setShowAddModal(false);
    } catch (error) {
      console.error("Error adding testimonial:", error);
    }
  };

  // Handle "Read more" click
  const handleReadMore = (testimonial) => {
    setModalContent(testimonial);
  };

  return (
    <>
      <SEO 
        title="Customer Testimonials | Gators Transportation Reviews"
        description="Read real testimonials from satisfied customers who have used Gators Transportation services in Jacksonville. See why clients trust us for their luxury transportation needs."
        keywords="Jacksonville transportation reviews, customer testimonials, client feedback, Gators Transportation reviews, satisfied customers"
        canonical="/testimonials"
      />
      <div className="testimonials-container">
        <section className="testimonials-section">
          <div className="testimonials-header">
            <h2 className="testimonials-title">💬 What Our Clients Say</h2>
            <h6 className="testimonials-subtitle">
              Real experiences from satisfied customers
            </h6>
            <button
              className="add-testimonial-btn"
              onClick={() => setShowAddModal(true)}
            >
              <span className="btn-icon">✨</span>
              Share Your Experience
            <span className="btn-arrow">→</span>
          </button>
        </div>

        <div className="testimonials-grid">
          {filteredTestimonials.map((testimonial) => (
            <div className="testimonial-card" key={testimonial.id}>
              <div className="testimonial-header">
                <div className="testimonial-avatar">
                  {testimonial.name.charAt(0).toUpperCase()}
                </div>
                <div className="testimonial-author">
                  <h5 className="testimonial-name">{testimonial.name}</h5>
                  <div className="testimonial-rating">
                    ⭐⭐⭐⭐⭐
                  </div>
                </div>
              </div>
              <div className="testimonial-content">
                <div className="quote-icon">❝</div>
                <p className="testimonial-text">
                  {testimonial.content.length > 150
                    ? `${testimonial.content.substring(0, 150)}...`
                    : testimonial.content}
                </p>
                {testimonial.content.length > 150 && (
                  <button
                    className="read-more-btn"
                    onClick={() => handleReadMore(testimonial)}
                  >
                    Read More
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

      {/* Modal for Displaying Full Testimonial */}
      {modalContent && (
        <div
          className="modal-overlay"
          onClick={() => setModalContent(null)}
        >
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-avatar">
                {modalContent.name.charAt(0).toUpperCase()}
              </div>
              <div className="modal-author">
                <h5 className="modal-name">{modalContent.name}</h5>
                <div className="modal-rating">⭐⭐⭐⭐⭐</div>
              </div>
              <button
                className="modal-close"
                onClick={() => setModalContent(null)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="quote-icon large">❝</div>
              <p className="modal-text">{modalContent.content}</p>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Adding a New Testimonial */}
      {showAddModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowAddModal(false)}
        >
          <div className="modal-container add-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">✨ Share Your Experience</h5>
              <button
                className="modal-close"
                onClick={() => setShowAddModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit} className="testimonial-form">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">
                    <span className="label-icon">👤</span>
                    Your Name
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    id="name"
                    name="name"
                    value={newTestimonial.name}
                    onChange={handleInputChange}
                    placeholder="Enter your name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="content" className="form-label">
                    <span className="label-icon">💭</span>
                    Your Experience
                  </label>
                  <textarea
                    className="form-textarea"
                    id="content"
                    name="content"
                    rows="4"
                    value={newTestimonial.content}
                    onChange={handleInputChange}
                    placeholder="Tell us about your experience with our transportation service..."
                    required
                  ></textarea>
                </div>
                <button type="submit" className="submit-btn">
                  <span className="btn-icon">🚀</span>
                  Submit Testimonial
                  <span className="btn-arrow">→</span>
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
      </section>
    </div>
    </>
  );
};

export default Testimonials;
